import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { formatPhone, timeOf, dayLabel, shortDate } from './threadUtils';

/**
 * The thread pane for a text from a number we could not match to a patient.
 * Left: the text as the patient sent it. Right: "Who is this?" with the
 * patients who share the number, a server-side search, and three actions:
 * assign, ask them to identify, or dismiss as not a patient.
 */
function PendingSenderView({ practiceId, pending, onAssigned, onDismissed, onAsked }) {
  const [matches, setMatches] = useState(null);
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setMatches(null); setTerm(''); setResults([]); setErr(null);
    api.lookupPatientsByPhone(practiceId, pending.from_phone)
      .then(r => { if (!cancelled) setMatches(Array.isArray(r?.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setMatches([]); });
    return () => { cancelled = true; };
  }, [practiceId, pending.id, pending.from_phone]);

  useEffect(() => {
    if (term.trim().length < 2) { setResults([]); return undefined; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(() => {
      api.getPatients(practiceId, { limit: 25, offset: 0, search: term.trim() })
        .then(r => { if (!cancelled) setResults(Array.isArray(r?.data) ? r.data : []); })
        .catch(() => { if (!cancelled) setResults([]); })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [term, practiceId]);

  const assign = async (patientId) => {
    setBusy(`assign-${patientId}`); setErr(null);
    try { await onAssigned(pending.id, patientId); }
    catch (e) { setErr(e.message || 'Could not assign'); }
    finally { setBusy(null); }
  };
  const ask = async () => {
    setBusy('ask'); setErr(null);
    try { await api.askSmsPendingToIdentify(practiceId, pending.id); onAsked?.(); }
    catch (e) { setErr(e.message || 'Could not send'); }
    finally { setBusy(null); }
  };
  const dismiss = async () => {
    const reason = window.prompt('Dismiss this text. Why? (optional: spam, wrong number, vendor…)', '');
    if (reason === null) return;
    setBusy('dismiss'); setErr(null);
    try { await api.dismissSmsPending(practiceId, pending.id, reason); onDismissed?.(pending.id); }
    catch (e) { setErr(e.message || 'Could not dismiss'); }
    finally { setBusy(null); }
  };

  const Row = ({ p, id }) => (
    <div className="who-row">
      <div style={{ minWidth: 0 }}>
        <div className="who-name">{p.first_name} {p.last_name}</div>
        <small>{[p.date_of_birth ? `DOB ${String(p.date_of_birth).slice(0, 10)}` : null, p.phone ? formatPhone(p.phone) : null, p.last_visit_date ? `last visit ${shortDate(p.last_visit_date)}` : null].filter(Boolean).join(' · ')}</small>
      </div>
      <button type="button" className="btn primary" disabled={!!busy} onClick={() => assign(id)}>{busy === `assign-${id}` ? '…' : 'Assign'}</button>
    </div>
  );

  return (
    <div className="nh-thread pending">
      <div className="nh-hdr">
        <div className="avatar" aria-hidden="true">?</div>
        <div className="who">
          <h3 className="name">Unidentified sender</h3>
          <div className="sub">
            <span className="mono">{formatPhone(pending.from_phone)}</span>
            <span className="nh-pill appt">texted {shortDate(pending.created_at)} {timeOf(pending.created_at)}</span>
            {pending.status === 'awaiting_reply' && <span className="nh-pill human">asked to identify{pending.disambiguation_reply_sent_at ? ` · ${shortDate(pending.disambiguation_reply_sent_at)}` : ''}</span>}
          </div>
        </div>
        <div className="actions">
          <button type="button" className="btn ghost" onClick={ask} disabled={!!busy}>{busy === 'ask' ? 'Sending…' : 'Ask them to identify'}</button>
          <button type="button" className="btn ghost" onClick={dismiss} disabled={!!busy}>Not a patient</button>
        </div>
      </div>

      <div className="nh-msgs">
        <div className="nh-day">{dayLabel(pending.created_at)}</div>
        <div className="nh-row patient">
          <div className="rail" />
          <div>
            <div className="nh-bub">{pending.content}</div>
            <div className="nh-meta"><span className="tag">Unknown number</span><span>{timeOf(pending.created_at)}</span></div>
          </div>
        </div>
        <div className="nh-event human">This text is not in any patient's thread yet. Assign it on the right, or ask the sender who they are.</div>
        {err && <div className="nh-event stop">{err}</div>}
      </div>

      <aside className="nh-rail" aria-label="Who is this?">
        <div className="rt"><span>Who is this?</span></div>
        <div className="nh-card who">
          <h4>📱 Share this number</h4>
          <div className="body">
            {matches === null && <small style={{ color: 'var(--t-ink3)' }}>Looking up the number…</small>}
            {matches && matches.length === 0 && <small style={{ color: 'var(--t-ink3)' }}>No patient record has this number.</small>}
            {matches && matches.map(p => <Row key={p.patient_id} p={p} id={p.patient_id} />)}
          </div>
        </div>
        <div className="nh-card who">
          <h4>🔎 Search patients</h4>
          <div className="body">
            <input className="who-search" value={term} onChange={e => setTerm(e.target.value)} placeholder="Name, phone or date of birth…" aria-label="Search patients" />
            {searching && <small style={{ color: 'var(--t-ink3)' }}>Searching…</small>}
            {!searching && term.trim().length >= 2 && results.length === 0 && <small style={{ color: 'var(--t-ink3)' }}>No matches.</small>}
            {results.map(p => <Row key={p.id} p={p} id={p.id} />)}
          </div>
        </div>
      </aside>

      <div className="nh-composer pending-foot">
        <small>Replies are not possible until this text is assigned to a patient.</small>
      </div>
    </div>
  );
}

export default PendingSenderView;
